import { ReactNode, useEffect, useState } from 'react';
import css from './BeforeYouScheduleStep.module.css';
import {
  CenterContent,
  H1,
  InformationBanner,
  Paragraph,
  StackedColumn,
  StackedColumnSpacing,
  TextLink,
  WarningBanner,
} from '../../../../core';
import { WizardStepWrapper } from '../step';
import { Question, QuestionAnswer } from './question';
import { SymptomsDialog } from './symptomsDialog';

export interface BeforeYouScheduleStepProps {
  onContinue: () => void;
}

const onChooseALocation = () => {
  window.open('https://www.marshfieldclinic.org/locations/search', '_blank');
};

const onViewPlans = () => {
  window.open(
    'https://www.marshfieldclinic.org/patient-resources/insurance/contracted-insurance-plans',
    '_blank'
  );
};

const workersCompHardStop = (
  <div>
    <Paragraph text="To comply with worker's compensation rules, please give us a call so we will schedule you with an appropriate provider in a timely matter to meet your needs. " />
    <TextLink caption="Choose a location" onClick={onChooseALocation} />
    <Paragraph text=" and call to schedule your appointment at your earliest convenience." />
  </div>
);

const outOfNetworkHardStop = (
  <div>
    <Paragraph text="Please contact your insurance carrier to learn more about in-network providers." />
  </div>
);

enum VAQuestionAnswer {
  Yes = 'Yes - I have a VA CCN authorization.',
  No_NoAuth = 'No - I am using VA CCN but do not have an authorization.',
  No_NoVACCN = 'No - I am not a veteran OR am not using VA CCN.',
}

const vAPrompt = (
  <div>
    <Paragraph text="Please bring your referral information as it will be required for your appointment." />
  </div>
);

const vAHardStop = (
  <div>
    <Paragraph text="All services paid for by the VA CCN must be authorized prior to seeing a provider having an associated test or procedure. If you have not already, we recommend you contact VA CCN at your assigned Veteran Affairs Medical Center (VAMC) to discuss the desired service and associated authorization." />
  </div>
);

const BeforeYouScheduleStep: React.FC<BeforeYouScheduleStepProps> = (
  props: BeforeYouScheduleStepProps
) => {
  const [validate, setValidate] = useState<boolean>(false);
  const [workersCompAnswer, setWorkersCompAnswer] = useState<string>();
  const [outOfNetworkAnswer, setOutOfNetworkAnswer] = useState<string>();
  const [vAApprovalAnswer, setVAApprovalAnswer] = useState<string>();
  const [allowContinue, setAllowContinue] = useState<boolean>(false);
  const [showSymptomsModal, setShowSymptomsModal] = useState<boolean>(false);
  const [hardStopContent, setHardStopContent] = useState<ReactNode | undefined>(
    undefined
  );
  const [continuePromptContent, setContinuePromptContent] = useState<
    ReactNode | undefined
  >(undefined);

  useEffect(() => {
    checkAllowContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workersCompAnswer, outOfNetworkAnswer, vAApprovalAnswer]);

  const onContinue = () => {
    if (validateAnswers()) {
      props.onContinue();
    }
  };

  const validateAnswers = (): boolean => {
    if (allowContinue) {
      setValidate(false);
      return true;
    }
    setValidate(true);
    return false;
  };

  const checkAllowContinue = () => {
    let allowContinue =
      workersCompAnswer !== undefined &&
      outOfNetworkAnswer !== undefined &&
      vAApprovalAnswer !== undefined;
    let hardStopContent: ReactNode | undefined = undefined;
    let continuePromptContent: ReactNode | undefined = undefined;

    if (workersCompAnswer === QuestionAnswer.Yes) {
      allowContinue = false;
      hardStopContent = workersCompHardStop;
    } else if (outOfNetworkAnswer === QuestionAnswer.No) {
      hardStopContent = outOfNetworkHardStop;
    } else if (vAApprovalAnswer === VAQuestionAnswer.Yes) {
      continuePromptContent = vAPrompt;
    } else if (vAApprovalAnswer === VAQuestionAnswer.No_NoAuth) {
      hardStopContent = vAHardStop;
    }

    if (hardStopContent !== undefined) {
      allowContinue = false;
    }

    setAllowContinue(allowContinue);
    setHardStopContent(hardStopContent);
    setContinuePromptContent(continuePromptContent);
  };

  const onViewConditionsClicked = () => {
    setShowSymptomsModal(true);
  };

  const onCloseSymptomsModal = () => {
    setShowSymptomsModal(false);
  };

  const beforeYouScheduleNoticeContent = (
    <div>
      <Paragraph text="Online scheduling is for non-urgent appointments. For urgent concerns, " />
      <TextLink
        caption="view our conditions or symptoms list"
        onClick={onViewConditionsClicked}
      />
      <Paragraph text=" for your best option for care." />
      {showSymptomsModal ? (
        <SymptomsDialog
          isOpen={showSymptomsModal}
          onClose={onCloseSymptomsModal}
        />
      ) : null}
      <br />
      <br />
      <Paragraph text="Make sure to check with your insurance before scheduling an appointment to see what's covered. If Marshfield Clinic Health System is not " />
      <TextLink
        caption="in-network for your insurance carrier,"
        onClick={onViewPlans}
      />
      <Paragraph text=" you may incur higher costs." />
    </div>
  );

  return (
    <WizardStepWrapper
      includeDetail={false}
      onContinue={
        allowContinue
          ? onContinue
          : workersCompAnswer === QuestionAnswer.Yes
          ? onChooseALocation
          : undefined
      }
      continueText={
        allowContinue
          ? undefined
          : workersCompAnswer === QuestionAnswer.Yes
          ? 'Choose a location'
          : undefined
      }
      stickyWhenNarrow={false}
    >
      <StackedColumn spacing={StackedColumnSpacing.Wider}>
        <CenterContent vertically={false}>
          <H1 text="Before you schedule..." />
        </CenterContent>
        <InformationBanner content={beforeYouScheduleNoticeContent} />
        <Question
          description={
            <Paragraph
              className={css.optionalText}
              small={true}
              text="A workman's compensation appointment is an evaluation with a medical professional to access and injury or illness that occurred on the job."
            />
          }
          question="Is this a work-related issue or injury?"
          answer={workersCompAnswer}
          groupName="workersComp"
          validate={validate}
          onChangeAnswer={(answer) => setWorkersCompAnswer(answer)}
        />
        {workersCompAnswer === QuestionAnswer.No ? (
          <Question
            description={
              <div>
                <Paragraph
                  className={css.optionalText}
                  small={true}
                  text="Marshfield Clinic Health System is unable to see patients with insurance plans that are outside of our contracted network. Please review the "
                />
                <TextLink
                  captionClassName={css.optionalTextLink}
                  caption="accepted insurance plans and network contracts"
                  onClick={onViewPlans}
                />
                <Paragraph
                  className={css.optionalText}
                  small={true}
                  text=" for more information."
                />
              </div>
            }
            question="Is your insurance provider part of our contracted network?"
            answer={outOfNetworkAnswer}
            groupName="outOfNetwork"
            validate={validate}
            onChangeAnswer={(answer) => setOutOfNetworkAnswer(answer)}
          />
        ) : null}
        {workersCompAnswer === QuestionAnswer.No &&
        outOfNetworkAnswer === QuestionAnswer.Yes ? (
          <Question
            question="Do you have a Veteran's Affairs Community Care Network (VA CCN) authorization for this service?"
            answer={vAApprovalAnswer}
            customAnswers={Object.values(VAQuestionAnswer)}
            groupName="vAApproval"
            validate={validate}
            onChangeAnswer={(answer) => setVAApprovalAnswer(answer)}
          />
        ) : null}
        {hardStopContent ? (
          <WarningBanner content={hardStopContent} />
        ) : undefined}
        {continuePromptContent ? (
          <InformationBanner content={continuePromptContent} />
        ) : undefined}
      </StackedColumn>
    </WizardStepWrapper>
  );
};

export default BeforeYouScheduleStep;
